import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError, setToken } from "@/lib/api";
import { SectionTitle } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RecoveryCodesView } from "@/components/settings/RecoveryCodes";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff, KeyRound, RefreshCw, AlertTriangle } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";

export function SecurityPanel() {
  const qc = useQueryClient();
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [codesOpen, setCodesOpen] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenPw, setRegenPw] = useState("");
  const [regenCode, setRegenCode] = useState("");

  const { data: status, isLoading } = useQuery({
    queryKey: ["mfa-status"],
    queryFn: async () => (await api.get("/auth/mfa/status")).data,
  });
  const enabled = !!status?.mfa_enabled;

  const { data: recovery } = useQuery({
    queryKey: ["mfa-recovery-status"],
    queryFn: async () => (await api.get("/auth/mfa/recovery/status")).data,
    enabled,
  });

  const showCodes = (codes) => { setRecoveryCodes(codes); setCodesOpen(true); };
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["mfa-status"] });
    qc.invalidateQueries({ queryKey: ["mfa-recovery-status"] });
  };

  const startSetup = useMutation({
    mutationFn: async () => (await api.post("/auth/mfa/setup")).data,
    onSuccess: (data) => { setEnroll(data); setCode(""); setSetupOpen(true); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const enable = useMutation({
    mutationFn: async () => (await api.post("/auth/mfa/enable", { code })).data,
    onSuccess: (data) => {
      refresh();
      setSetupOpen(false); setEnroll(null); setCode("");
      toast.success("Two-factor authentication enabled");
      showCodes(data.recovery_codes || []);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const disable = useMutation({
    mutationFn: async () => (await api.post("/auth/mfa/disable", { current_password: disablePw, code: disableCode })).data,
    onSuccess: (data) => {
      if (data.access_token) setToken(data.access_token);
      refresh();
      setDisableOpen(false); setDisablePw(""); setDisableCode("");
      toast.success("Two-factor authentication disabled");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const regen = useMutation({
    mutationFn: async () => (await api.post("/auth/mfa/recovery/regenerate", { current_password: regenPw, code: regenCode })).data,
    onSuccess: (data) => {
      refresh();
      setRegenOpen(false); setRegenPw(""); setRegenCode("");
      toast.success("New recovery codes generated");
      showCodes(data.recovery_codes || []);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const remaining = recovery?.remaining ?? 0;
  const total = recovery?.total ?? 10;
  const low = !!recovery?.low;

  return (
    <div className="mt-6 rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5" data-testid="security-panel">
      <SectionTitle className="mb-4">Security · Two-Factor Authentication</SectionTitle>
      {isLoading ? (
        <div className="flex h-16 items-center"><Loader2 className="h-5 w-5 animate-spin text-cyan-400" /></div>
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            {enabled ? <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-400" /> : <ShieldOff className="mt-0.5 h-5 w-5 text-slate-500" />}
            <div>
              <p className="text-sm font-medium text-slate-200" data-testid="mfa-status-label">
                {enabled ? "2FA is enabled" : "2FA is not enabled"}
              </p>
              <p className="text-xs text-slate-500">
                {enabled
                  ? "Your account requires an authenticator code at sign in."
                  : "Add an authenticator app (Google Authenticator, Authy, 1Password) for stronger login security."}
              </p>
            </div>
          </div>
          {enabled ? (
            <Button variant="outline" className="border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10"
              onClick={() => setDisableOpen(true)} data-testid="mfa-disable-open">
              <ShieldOff className="mr-1.5 h-4 w-4" /> Disable 2FA
            </Button>
          ) : (
            <Button className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500"
              onClick={() => startSetup.mutate()} disabled={startSetup.isPending} data-testid="mfa-enable-open">
              {startSetup.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <KeyRound className="mr-1.5 h-4 w-4" />} Enable 2FA
            </Button>
          )}
        </div>
      )}

      {enabled && (
        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-[#1E2D4A] bg-[#0B1120]/60 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-slate-200">Recovery codes</p>
            <p className="text-xs text-slate-500" data-testid="recovery-remaining-label">
              {remaining} of {total} codes remaining
            </p>
            {low && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-300" data-testid="recovery-low-warning">
                <AlertTriangle className="h-3.5 w-3.5" /> Running low — generate a new set soon.
              </p>
            )}
          </div>
          <Button variant="outline" className="border-cyan-500/30 bg-transparent text-cyan-300 hover:bg-cyan-500/10"
            onClick={() => setRegenOpen(true)} data-testid="recovery-regenerate-open">
            <RefreshCw className="mr-1.5 h-4 w-4" /> Generate new recovery codes
          </Button>
        </div>
      )}

      {/* MFA setup dialog */}
      <Dialog open={setupOpen} onOpenChange={(o) => { setSetupOpen(o); if (!o) { setEnroll(null); setCode(""); } }}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-md" data-testid="mfa-setup-dialog">
          <DialogHeader><DialogTitle>Set up two-factor authentication</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.</p>
            {enroll?.qr_data_uri && (
              <div className="flex justify-center">
                <img src={enroll.qr_data_uri} alt="MFA QR code" className="h-44 w-44 rounded-lg bg-white p-2" data-testid="mfa-qr-image" />
              </div>
            )}
            {enroll?.manual_key && (
              <div className="rounded-lg border border-[#2D3F62] bg-[#0B1120] px-3 py-2">
                <p className="text-xs text-slate-500">Manual entry key</p>
                <p className="break-all font-mono text-xs text-cyan-300" data-testid="mfa-manual-key">{enroll.manual_key}</p>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-400">Authenticator code</Label>
              <Input inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="6-digit code" className={`mt-1 tracking-widest ${inputCls}`} data-testid="mfa-enable-code-input" />
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 font-semibold text-slate-900"
              onClick={() => enable.mutate()} disabled={enable.isPending || code.length !== 6} data-testid="mfa-enable-confirm">
              {enable.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />} Verify & Enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recovery codes display dialog — cannot dismiss until confirmed saved */}
      <Dialog open={codesOpen} onOpenChange={(o) => { if (o) setCodesOpen(true); }}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-md [&>button]:hidden" data-testid="recovery-codes-dialog">
          <DialogHeader><DialogTitle>Your recovery codes</DialogTitle></DialogHeader>
          <RecoveryCodesView codes={recoveryCodes} onDone={() => { setCodesOpen(false); setRecoveryCodes([]); }} />
        </DialogContent>
      </Dialog>

      {/* Regenerate recovery codes dialog */}
      <Dialog open={regenOpen} onOpenChange={(o) => { setRegenOpen(o); if (!o) { setRegenPw(""); setRegenCode(""); } }}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-md" data-testid="recovery-regenerate-dialog">
          <DialogHeader><DialogTitle>Generate new recovery codes</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">This invalidates all your existing recovery codes. Confirm your password and an authenticator or recovery code.</p>
            <div>
              <Label className="text-xs text-slate-400">Current password</Label>
              <Input type="password" value={regenPw} onChange={(e) => setRegenPw(e.target.value)}
                placeholder="••••••••" className={`mt-1 ${inputCls}`} data-testid="recovery-regenerate-password" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Authenticator or recovery code</Label>
              <Input value={regenCode} onChange={(e) => setRegenCode(e.target.value)}
                placeholder="6-digit code or recovery code" className={`mt-1 ${inputCls}`} data-testid="recovery-regenerate-code" />
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900"
              onClick={() => regen.mutate()} disabled={regen.isPending || !regenPw || !regenCode} data-testid="recovery-regenerate-confirm">
              {regen.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Generate new codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA dialog */}
      <Dialog open={disableOpen} onOpenChange={(o) => { setDisableOpen(o); if (!o) { setDisablePw(""); setDisableCode(""); } }}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-md" data-testid="mfa-disable-dialog">
          <DialogHeader><DialogTitle>Disable two-factor authentication</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Confirm your password and a current authenticator or recovery code to disable 2FA. This also invalidates your recovery codes.</p>
            <div>
              <Label className="text-xs text-slate-400">Current password</Label>
              <Input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)}
                placeholder="••••••••" className={`mt-1 ${inputCls}`} data-testid="mfa-disable-password" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Authenticator or recovery code</Label>
              <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
                placeholder="6-digit code or recovery code" className={`mt-1 ${inputCls}`} data-testid="mfa-disable-code-input" />
            </div>
          </div>
          <DialogFooter>
            <Button className="w-full bg-gradient-to-r from-rose-500 to-red-600 font-semibold text-white"
              onClick={() => disable.mutate()} disabled={disable.isPending || !disablePw || !disableCode} data-testid="mfa-disable-confirm">
              {disable.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-1.5 h-4 w-4" />} Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
